import React, { useCallback, useEffect, useContext } from "react";
import NavListItem from "../NavListItem";
import HeaderLink from "../HeaderLink";
import isMobile from "@src/utils/isMobile";
import Translate from "@src/components/translation/Translate";
import { FlexProps } from "@src/components/Boxes";
import DownloadAppButton from "../DownloadAppButton";
import LoginPart from "../LoginPart";
import * as S from "./styles";
import { debounce, isNil } from "lodash";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { HeaderHTMLA } from "../HeaderLink/styles";
import { logEvent } from "@src/utils/logClient";
import { get } from "cookies";
import { path } from "lodash/fp";
import { getRelativePath } from "@src/utils/routes";
import styled from "@emotion/styled";
import { mediaQueries, mediaQueriesMax } from "@src/styles/theme";
import { Link } from "gatsby";
import { ListItem } from "../NavListItem";
import { List } from "vendor/classdojo-web/dist";

// Styled component for consistent list item styling
const SimpleListItem = styled("li")`
  padding: 10px 15px;
  list-style: none;
  margin-top: 0;
  ${mediaQueriesMax[2]} {
    width: 100%;
  }
  ${mediaQueries[2]} {
    padding: 0;
  }
`;

type NavLinkProps = FlexProps & {
  openLoginModal: () => void;
  openSignupModal: () => void;
  closeMenu: () => void;
  active?: boolean;
  mobile?: boolean;
};

const NavLinks: React.FC<NavLinkProps> = ({ openLoginModal, openSignupModal, closeMenu, ...props }) => {
  const handleOpenLogin = useCallback(() => {
    closeMenu();
    openLoginModal();
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.login.tap",
    });
  }, [closeMenu, openLoginModal]);

  const handleOpenSignup = useCallback(() => {
    closeMenu();
    openSignupModal();
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.signup.tap",
    });
  }, [closeMenu, openSignupModal]);

  const { getIsCountryUS } = useContext(TranslationContext);

  const isCountryUS = getIsCountryUS();
  const isUserInMobile = isMobile().any;

  // Helper function to handle clicks for simple links
  const handleLinkClick = useCallback(
    (eventName: string) => {
      return () => {
        closeMenu();
        logEvent({
          eventValue: window.location.href,
          eventName,
        });
      };
    },
    [closeMenu],
  );

  // This is necessary so the focus doesn't stay trapped in the nav
  // if the menu is open on small screen and the user resizes it to a bigger width
  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      closeMenu();
    }, 1000);

    window.addEventListener("resize", debouncedHandleResize);

    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let downloadButtonHref, downloadTranslationPath;

  if (isMobile().apple.tablet) {
    downloadButtonHref = "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8";
    downloadTranslationPath = "header.nav.get_ipad_app";
  } else if (isMobile().apple.phone) {
    downloadButtonHref = "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8";
    downloadTranslationPath = "header.nav.get_iphone_app";
  } else if (isMobile().android.device) {
    downloadButtonHref = "https://play.google.com/store/apps/details?id=com.classdojo.android";
    downloadTranslationPath = "header.nav.get_android_app";
  }

  const handleKeyDown = (event: { key: string; preventDefault: () => void; stopPropagation: () => void }) => {
    if (event.key === "Escape") closeMenu();
    const currentListItem = document.activeElement?.parentElement as HTMLLIElement;
    const parentList = currentListItem.parentNode;
    const previousElement = currentListItem.previousElementSibling;
    const nextElement = currentListItem.nextElementSibling;

    let targetListItem;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (previousElement) targetListItem = previousElement;
      else targetListItem = parentList?.lastElementChild;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (nextElement) targetListItem = nextElement;
      else targetListItem = parentList?.firstElementChild;
    }
    if (targetListItem) (targetListItem.firstElementChild as HTMLElement)?.focus();
    event.stopPropagation();
  };

  return (
    <S.NavLinksContainer active={props.active} aria-hidden={!props.active} onKeyDown={handleKeyDown}>
      {/* Products - with dropdown */}
      {isCountryUS && (
        <NavListItem
          translationPath="layouts.main.products"
          path=""
          onClick={closeMenu}
          eventName="web.external_page.navbar.products.tap"
          isNarrow={true}
          sublinks={[
            {
              eventName: "web.external_page.navbar.products.classdojo.tap",
              ctaTranslated: "layouts.main.submenu_products_classdojo.cta",
              path: "/?utm_source=newnav",
            },
            {
              eventName: "web.external_page.navbar.products.plus.tap",
              ctaTranslated: "layouts.main.classdojo_plus",
              path: "/plus/?utm_source=newnav",
            },
            {
              eventName: "web.external_page.navbar.products.islands.tap",
              ctaTranslated: "layouts.main.submenu_products_dojo_islands.cta",
              path: "/dojo-islands/parents?utm_source=newnav",
            },
            {
              eventName: "web.external_page.navbar.products.tutor.tap",
              ctaTranslated: "layouts.main.submenu_products_dojo_tutor.cta",
              path: "https://tutor.classdojo.com/?utm_source=newnav",
            },
            {
              eventName: "web.external_page.navbar.products.sparks.tap",
              ctaTranslated: "layouts.main.submenu_products_dojo_sparks.cta",
              path: "/sparks?utm_source=newnav",
            },
          ]}
        >
          <S.LinkText>
            <Translate path="layouts.main.products" />
          </S.LinkText>
        </NavListItem>
      )}

      {/* Teachers - simple link */}
      <ListItem>
        <Link to={getRelativePath("/teachers/")} onClick={handleLinkClick("web.external_page.navbar.teachers.tap")}>
          <S.LinkText>
            <Translate path="layouts.main.teachers" />
          </S.LinkText>
        </Link>
      </ListItem>

      {/* Schools - simple link */}
      <ListItem>
        <Link to={getRelativePath("/schools/")} onClick={handleLinkClick("web.external_page.navbar.schools.tap")}>
          <S.LinkText>
            <Translate path="layouts.main.schools" />
          </S.LinkText>
        </Link>
      </ListItem>

      {/* Districts - conditional on US only */}
      {isCountryUS && (
        <div className="conditional-link">
          <ListItem>
            <Link to="/districts/" onClick={handleLinkClick("web.external_page.navbar.districts.tap")}>
              <S.LinkText>
                <Translate path="layouts.main.districts" />
              </S.LinkText>
            </Link>
          </ListItem>
        </div>
      )}

      {/* Families - conditional on US only */}

      <div className="conditional-link">
        {isCountryUS && (
          <ListItem>
            <Link to="/families/" onClick={handleLinkClick("web.external_page.navbar.families.tap")}>
              <S.LinkText>
                <Translate path="layouts.main.families" />
              </S.LinkText>
            </Link>
          </ListItem>
        )}
      </div>

      {/* Resources - with dropdown */}
      <div className="conditional-link">
        <NavListItem
          translationPath="layouts.main.support.resources"
          path=""
          onClick={closeMenu}
          eventName="web.external_page.navbar.resources.tap"
          sublinks={[
            {
              ctaTranslated: "layouts.main.submenu_training.cta",
              descriptionTranslated: "layouts.main.submenu_training.description",
              path: "/training/?utm_source=newnav",
              eventName: "web.external_page.navbar.training.tap",
            },
            {
              ctaTranslated: "layouts.main.submenu_resources.cta",
              descriptionTranslated: "layouts.main.submenu_resources.description",
              path: "/resources/?utm_source=newnav",
              eventName: "web.external_page.navbar.resources.tap",
            },
            {
              ctaTranslated: "layouts.main.submenu_big_ideas.cta",
              descriptionTranslated: "layouts.main.submenu_big_ideas.description",
              path: "https://ideas.classdojo.com/?utm_source=newnav",
              eventName: "web.external_page.navbar.big_ideas.tap",
            },
            {
              ctaTranslated: "layouts.main.submenu_activity_corner.cta",
              descriptionTranslated: "layouts.main.submenu_activity_corner.description",
              path: "/activity-corner/?utm_source=newnav",
              eventName: "web.external_page.navbar.activity_corner.tap",
            },
            {
              ctaTranslated: "layouts.main.submenu_sl_resources.cta",
              descriptionTranslated: "layouts.main.submenu_sl_resources.description",
              path: "/schoolleader-resources/?utm_source=newnav",
              eventName: "web.external_page.navbar.schoolleader_resources.tap",
            },
          ]}
        >
          <S.LinkText>
            <Translate path="layouts.main.support.resources" />
          </S.LinkText>
        </NavListItem>
      </div>

      <LoginPart openLoginModal={handleOpenLogin} openSignupModal={handleOpenSignup} />
      {/* TODO extract this device checking to a central place */}
      {typeof window !== "undefined" && (
        <>
          {downloadButtonHref && downloadTranslationPath && (
            <DownloadAppButton buttonHref={downloadButtonHref} translationPath={downloadTranslationPath} />
          )}
        </>
      )}
    </S.NavLinksContainer>
  );
};

export default NavLinks;
